import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import { toast } from "react-toastify";
import { MonthlyDailyReport } from "../../Services/Dashboard";
import SpinnerModal from "../../Spinner/SpinnerModal";
import { Button } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DateRangeIcon from "@mui/icons-material/DateRange";
import "./Reports.css";

const MonthlyReports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [monthlyReports, setMonthlyReports] = useState([]);
  const [FilterYear, setYear] = useState(new Date().getFullYear().toString());

  // Fetch Monthly Reports
  const GetMonthlyDaily = async (Year) => {
    try {
      setIsLoading(true);
      const LabID = sessionStorage.getItem("LabID");
      const response = await MonthlyDailyReport(LabID, Year);
      setMonthlyReports(response.monthlyReports || []);
    } catch (error) {
      toast.error("Unable to get data", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const currentDate = new Date();
    const Year = currentDate.getFullYear();
    setYear(Year.toString());
    GetMonthlyDaily(Year);
  }, []);

  // Filter handlers
  const handleChange = (e) => {
    const selectedYear = e.target.value;
    setYear(selectedYear);
  };

  const Applyfilter = () => {
    GetMonthlyDaily(FilterYear);
  };

  // Define columns for the data table
  const columns = [
    {
      name: "Month",
      selector: (row) => row.monthName,
      cellExport: (row) => row.monthName,
      sortable: true,
      cell: (row) => (
        <span className="commont-Font-Bold text-dark">{row.monthName}</span>
      ),
    },
    {
      name: "Tests",
      selector: (row) => row.totalTest,
      cellExport: (row) => row.totalTest,
      sortable: true,
      right: true,
      cell: (row) => (
        <span className="badge bg-primary-subtle common-color commont-Font-Bold px-2 py-1">
          {row.totalTest || 0}
        </span>
      ),
    },
    {
      name: "Sell",
      selector: (row) => row.totalGrandTotal,
      cellExport: (row) => row.totalGrandTotal,
      sortable: true,
      right: true,
      cell: (row) => (
        <span className="common-FontWeight text-dark">
          {Number(row.totalGrandTotal || 0).toLocaleString()}
        </span>
      ),
    },
    {
      name: "Discount",
      selector: (row) => row.totalDiscount,
      cellExport: (row) => row.totalDiscount,
      sortable: true,
      right: true,
      cell: (row) => (
        <span className="text-muted">
          {Number(row.totalDiscount || 0).toLocaleString()}
        </span>
      ),
    },
    {
      name: "Received Amount",
      selector: (row) => row.receivedAmount,
      cellExport: (row) => row.receivedAmount,
      sortable: true,
      right: true,
      cell: (row) => (
        <span className="commont-Font-Bold text-success">
          {Number(row.receivedAmount || 0).toLocaleString()}
        </span>
      ),
    },
    {
      name: "Due Amount",
      selector: (row) => row.dueAmount,
      cellExport: (row) => row.dueAmount,
      sortable: true,
      right: true,
      cell: (row) => (
        <span
          className={`commont-Font-Bold ${
            Number(row.dueAmount) > 0 ? "text-danger" : "text-secondary"
          }`}
        >
          {Number(row.dueAmount || 0).toLocaleString()}
        </span>
      ),
    },
  ];

  const tableData = {
    columns,
    data: monthlyReports,
  };

  const customStyles = {
    header: {
      style: {
        minHeight: "56px",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8fafc",
        borderTop: "1px solid #e2e8f0",
        borderBottom: "2px solid #cbd5e1",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: "600",
        color: "#475569",
        paddingLeft: "16px",
        paddingRight: "16px",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      },
    },
    rows: {
      style: {
        fontSize: "12px",
        color: "#1e293b",
        minHeight: "48px",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        "&:not(:last-child)": {
          borderBottomStyle: "solid",
          borderBottomWidth: "1px",
          borderBottomColor: "#f1f5f9",
        },
        "&:hover": {
          backgroundColor: "#f8fafc !important",
          transition: "all 0.2s ease",
        },
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e2e8f0",
        fontSize: "12px",
      },
    },
  };

  return (
    <>
      {isLoading && <SpinnerModal isLoading={isLoading} />}
      <div className="reports-container common-FontFamily">
        {/* Filter Toolbar */}
        <div className="row align-items-center justify-content-between g-3 pb-3 mb-3 border-bottom">
          <div className="col-auto d-flex align-items-center gap-2">
            <DateRangeIcon className="common-color fs-5" />
            <h6 className="mb-0 commont-Font-Bold text-dark common-FontSize-small">
              Monthly Revenue Overview
            </h6>
          </div>

          <div className="col-auto d-flex align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="Year" className="common-Labels mb-0">
                Year Filter<span className="common-Asterisk">*</span>
              </label>
              <select
                className="form-select common-Input"
                id="Year"
                name="Year"
                value={FilterYear}
                required
                onChange={handleChange}
                style={{ width: "110px" }}
              >
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
              </select>
            </div>

            <Button
              variant="contained"
              onClick={Applyfilter}
              type="button"
              className="common-button text-capitalize"
              startIcon={<FilterListIcon />}
              style={{ backgroundColor: "#2c6bd7", minWidth: "80px" }}
            >
              Apply
            </Button>
          </div>
        </div>

        {/* Data Table */}
        <DataTableExtensions {...tableData} filter={false} export={false} print={false}>
          <DataTable
            pagination
            fixedHeader
            fixedHeaderScrollHeight="70vh"
            highlightOnHover
            pointerOnHover
            responsive
            striped
            customStyles={customStyles}
            noDataComponent={
              <div className="p-4 common-text-center text-muted common-FontSize-small">
                No monthly records found for the selected year.
              </div>
            }
          />
        </DataTableExtensions>
      </div>
    </>
  );
};

export default MonthlyReports;